import React, { useContext, useEffect } from 'react'
import Script from 'next/script'
import { QualifiedEventName, QualifiedMessengerEventName } from './types'
import { getConfig } from '../../../config'
import { gnarSpec } from '../../../lib/tracking/gnar'
import { logError, LogErrorMetrics } from '../../../lib/tracking/felog'
import { transformToString } from '../../../lib/utils/error'
import { PageContext } from '../../../contexts/page-context'

const { token, url } = getConfig().qualified

const qualifiedScript = `(function(w,q){w['QualifiedObject']=q;w[q]=w[q]||function(){
(w[q].q=w[q].q||[]).push(arguments)};})(window,'qualified')`

const scriptExists = (id: 'qualified-script' | 'qualified-js-script'): boolean => {
  if (IS_CLIENT_SIDE) {
    const script = document.querySelector(`script[id="${id}"]`)
    return Boolean(script)
  }
  return false
}

const addTracking = (pageId: string, slug: string) => {
  const qualified = window.qualified

  qualified?.('handleEvents', (eventName: QualifiedEventName) => {
    gnarSpec.messengerQualifiedInteraction(eventName, pageId, slug)
  })

  qualified?.('handleMessengerEvents', (eventName: QualifiedMessengerEventName) => {
    switch (eventName) {
      case QualifiedMessengerEventName.MessengerOpened:
        gnarSpec.messengerQualifiedInteraction('Open', pageId, slug)
        break
      case QualifiedMessengerEventName.MessagesDismissed:
        gnarSpec.messengerQualifiedInteraction('Dismiss', pageId, slug)
        break
      case QualifiedMessengerEventName.MessengerClosed:
        gnarSpec.messengerQualifiedInteraction('Close', pageId, slug)
        break
      default:
        gnarSpec.messengerQualifiedInteraction('Other', pageId, slug)
        break
    }
  })
}

export const QualifiedScript: React.FC = () => {
  const qualifiedJsUrl = `${url}?token=${token}`
  const { pageId, slug } = useContext(PageContext)

  useEffect(() => {
    window.qualified?.('page')
    addTracking(pageId, slug)
  }, [pageId, slug])

  const logScriptLoadingError = (err: Error) => {
    logError(`Error loading qualified script: ${transformToString(err)}`, LogErrorMetrics.Qualified)
  }

  return (
    <>
      {!scriptExists('qualified-script') && (
        <Script
          id="qualified-script"
          type="text/javascript"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{ __html: qualifiedScript }}
        />
      )}
      {!scriptExists('qualified-js-script') && (
        <Script
          async
          defer
          id="qualified-js"
          src={qualifiedJsUrl}
          strategy="afterInteractive"
          onError={logScriptLoadingError}
        />
      )}
    </>
  )
}

export default QualifiedScript
